import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Icon } from "../../../../components/icons/icon";
import { TabNav } from "../../../../components/navs/tabNav";
import {
  useGetMembershipProfileQuery,
  useGetSingleCooperativeDetailsQuery,
} from "../../../../redux/features/cooperative/cooperative.api";
import { PermissionCode } from "../../../../redux/features/settings/settings.type";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../utils/constants";

import { EnforceDirectDebitSetupModal } from "../../../../components/modals/app_modals/debitInstrument/enforceDirectDebitSetupModal";
import { CooperativePageTab } from "../../../../utils/types";
import { CooperativeSavings } from "./contributions";
import {
  CooperativeOverview,
  CooperativeOverviewLoader,
} from "./cooperativeOverview";
import { CooperativeFinances } from "./finances";
import { CooperativeLoans } from "./loans";
import { CooperativeMembers } from "./members";

export type Mode = "personal" | "admin";

export const SingleMyCooperative = () => {
  const [activeTab, setActiveTab] = useState<CooperativePageTab>("overview");
  const [mode, setMode] = useState<Mode>("personal");
  const [navs, setNavs] = useState<
    {
      title: string;
      to: CooperativePageTab;
    }[]
  >([]);
  const [cooperativeConfig, setCooperativeConfig] = useState<{
    depositsActive: boolean;
    sharesActive: boolean;
    permissions: PermissionCode[];
  }>({
    depositsActive: false,
    sharesActive: false,
    permissions: [],
  });

  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: memberProfile,
    isLoading: fetchingMemberProfile,
    refetch: refetchMemberProfile,
  } = useGetMembershipProfileQuery(
    {
      coop: id!,
      Permission: true,
      collection: true,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    }
  );

  const { data: cooperativeDetails, isLoading: fetchingCooperativeDetails } =
    useGetSingleCooperativeDetailsQuery(
      {
        id: id!,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !id,
      }
    );

  const modeNavs = [
    {
      title: "Personal",
      to: "personal",
      onClick: () => {
        setSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          mode: "personal",
        });
      },
    },
    {
      title: "Admin",
      to: "admin",
      onClick: () => {
        setSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          mode: "admin",
        });
      },
    },
  ];

  useEffect(() => {
    if (memberProfile?.data && cooperativeDetails?.data) {
      const memProfile = memberProfile.data;
      const coopDetails = cooperativeDetails.data;

      const depositsActive =
        !!coopDetails?.Settings?.deposit?.InterestRate &&
        !!coopDetails?.Settings?.deposit?.NumberOfDaysToWithdrawal;
      const sharesActive =
        !!coopDetails?.Settings?.share?.InitialSharePrice &&
        !!coopDetails?.Settings?.share?.InterestRate &&
        !!coopDetails?.Settings?.share?.MinShares;
      const permissions =
        memProfile?.Role?.permissions?.map((p) => p.CodeName) ?? [];

      setCooperativeConfig({
        depositsActive: depositsActive,
        sharesActive: sharesActive,
        permissions: permissions,
      });

      const allNavs: {
        title: string;
        to: CooperativePageTab;
      }[] = [
        {
          title: "Overview",
          to: "overview",
        },
        {
          title: "Contributions",
          to: "contributions",
        },
        // {
        //   title: "Deposits",
        //   to: "deposits",
        // },
        // {
        //   title: "Shares",
        //   to: "shares",
        // },
        {
          title: "Loans",
          to: "loans",
        },
        {
          title: "Members",
          to: "members",
        },

        {
          title: "Finances",
          to: "finances",
        },
      ];

      setNavs(
        allNavs.filter((nav) => {
          if (!sharesActive && nav.to === "shares") {
            return false;
          }
          if (!depositsActive && nav.to === "deposits") {
            return false;
          }
          if (permissions.length < 1 && nav.to === "finances") {
            return false;
          }

          return true;
        })
      );

      if (!memProfile.isApprove) {
        // Navigate out of screen if member has not been approved
        navigate("/app/my-cooperatives");
      }
    }
    // eslint-disable-next-line
  }, [cooperativeDetails, memberProfile]);

  useEffect(() => {
    setActiveTab((searchParams.get("tab") ?? "overview") as CooperativePageTab);
    setMode((searchParams.get("mode") ?? "personal") as Mode);
  }, [searchParams]);

  return (
    <>
      <div>
        {fetchingCooperativeDetails || fetchingMemberProfile ? (
          <div className="flex flex-col gap-6 w-full">
            <Skeleton
              width={"100px"}
              height={24}
              baseColor={SKELETON_BASE_COLOR}
              highlightColor={SKELETON_HIGHLIGHT_COLOR}
            />

            <Skeleton
              width={"100%"}
              height={32}
              baseColor={SKELETON_BASE_COLOR}
              highlightColor={SKELETON_HIGHLIGHT_COLOR}
            />

            <CooperativeOverviewLoader />
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap gap-4">
              <p className="text-pry-green-200 flex-1 font-medium text-base flex flex-row flex-wrap gap-1 items-center max-md:hidden">
                <Link
                  className="text-pry-dark-green-300/50"
                  to={"/app/my-cooperatives"}
                >
                  My Cooperatives
                </Link>

                <Icon
                  svg="left-caret-icon"
                  width={24}
                  height={24}
                  className="rotate-180"
                />

                <span>{cooperativeDetails?.data?.CoopName}</span>
              </p>

              {cooperativeConfig.permissions.length === 0 ? undefined : (
                <TabNav
                  navs={modeNavs}
                  activeTab={mode}
                  className={`w-full md:max-w-[264px]`}
                  variant="switcher"
                  theme="white"
                />
              )}
            </div>

            <TabNav
              navs={navs.map((n) => {
                return {
                  ...n,
                  onClick: () => {
                    setSearchParams({
                      ...Object.fromEntries(searchParams.entries()),
                      tab: n.to,
                    });
                  },
                };
              })}
              className="mt-5 bg-light-green-bg w-full md:w-fit overflow-scroll"
              activeTab={activeTab}
              variant="nav"
            />

            <div className="mt-6 w-full ">
              {activeTab === "overview" &&
              cooperativeDetails?.data &&
              navs.map((n) => n.to).includes("overview") ? (
                <CooperativeOverview
                  cooperativeDetails={cooperativeDetails.data}
                  memberProfile={memberProfile}
                  refetchMemberProfile={() => {
                    refetchMemberProfile();
                  }}
                />
              ) : undefined}

              {activeTab === "contributions" &&
              cooperativeDetails?.data &&
              navs.map((n) => n.to).includes("contributions") ? (
                <CooperativeSavings
                  cooperativeDetails={cooperativeDetails.data}
                  mode={mode}
                  permissions={cooperativeConfig.permissions}
                />
              ) : undefined}

              {/* {activeTab === "deposits" &&
              cooperativeDetails?.data &&
              navs.map((n) => n.to).includes("contributions") ? (
                <CooperativeDeposits
                  cooperativeDetails={cooperativeDetails.data}
                  mode={mode}
                  permissions={cooperativeConfig.permissions}
                />
              ) : undefined}

              {activeTab === "shares" &&
              cooperativeDetails?.data &&
              navs.map((n) => n.to).includes("shares") ? (
                <CooperativeShares
                  cooperativeDetails={cooperativeDetails.data}
                  mode={mode}
                  permissions={cooperativeConfig.permissions}
                />
              ) : undefined} */}

              {activeTab === "members" &&
              cooperativeDetails?.data &&
              navs.map((n) => n.to).includes("members") ? (
                <CooperativeMembers
                  cooperativeDetails={cooperativeDetails.data}
                  mode={mode}
                  permissions={cooperativeConfig.permissions}
                />
              ) : undefined}

              {activeTab === "loans" &&
              cooperativeDetails?.data &&
              navs.map((n) => n.to).includes("loans") ? (
                <CooperativeLoans
                  cooperativeDetails={cooperativeDetails.data}
                  mode={mode}
                  permissions={cooperativeConfig.permissions}
                />
              ) : undefined}

              {activeTab === "finances" &&
              cooperativeDetails?.data &&
              navs.map((n) => n.to).includes("finances") ? (
                <CooperativeFinances
                  cooperativeDetails={cooperativeDetails.data}
                  mode={mode}
                  permissions={cooperativeConfig.permissions}
                />
              ) : undefined}
            </div>
          </div>
        )}
      </div>

      {cooperativeDetails?.data ? (
        <EnforceDirectDebitSetupModal
          isModalOpen={
            cooperativeDetails.data.Settings.settings.isDirectDebitEnabled &&
            (!memberProfile?.data?.collection?.Detail ||
              !memberProfile?.data?.collection?.paymentCollectionType)
          }
          cooperativeDetails={cooperativeDetails.data}
          type="new"
          onSuccess={() => {
            refetchMemberProfile();
          }}
        />
      ) : undefined}
    </>
  );
};
